import {
    InstrumentListState,
    InstrumentList,
    InstrumentUploadResponse,
    PartnerEmployerInstrumentList,
    EmployerDrivewealthListRequest,
    CustodianDrivewealthListRequest,
    ListType
} from "@/admin/types";
import {
    FetchState,
    InstrumentDetail,
    DrivewealthAccountManagementType,
} from "@/shared/types";
import _ from "lodash";
import * as drivewealthService from "@/shared/api/drivewealthService";
import * as storeHelper from "@/shared/storeHelper";
import { downloadCsvFile } from "@/shared/fileDownload";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: InstrumentListState = {
    curatedRtqInstrumentListFetchState: FetchState.Uninitialized,
    instrumentListFetchState: FetchState.Uninitialized,
    instrumentDetailFetchState: FetchState.Uninitialized,
    instrumentUploadResponse: null,
    instrumentLists: [],
    curatedRtqInstrumentLists: [],
    instrumentDetails: [],
    employerInstrumentList: null,
    employerInstrumentListFetchState: FetchState.Uninitialized,
    partnerInstrumentList: null,
    partnerInstrumentListFetchState: FetchState.Uninitialized,
    wlpInstrumentListsFetchState: FetchState.Uninitialized,
    custodianWlpInstrumentListsFetchState: FetchState.Uninitialized,
    employerDefaultInstrumentLists: [],
    custodianDefaultInstrumentLists: []
};

export const useInstrumentListStore = defineStore("instrumentList", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        didLoadInstrumentLists: state => state.instrumentListFetchState === FetchState.Loaded,
        didLoadCuratedRtqInstrumentLists: state => state.curatedRtqInstrumentListFetchState === FetchState.Loaded,
        didLoadtInstrumentListDetails: state => state.instrumentDetailFetchState === FetchState.Loaded,
        instrumentDetailsIsLoading: state => state.instrumentDetailFetchState === FetchState.Loading,
        didLoadEmployerInstrumentList: state => state.employerInstrumentListFetchState === FetchState.Loaded,
        didLoadPartnerInstrumentList: state => state.partnerInstrumentListFetchState === FetchState.Loaded,
        advisoryInstrumentLists: state => {
            return _.filter(state.instrumentLists,
                x => x.listInvestmentOption === DrivewealthAccountManagementType.Advisory);
        },
        openBrokerageInstrumentLists: state => {
            return _.filter(state.instrumentLists,
                x => x.listInvestmentOption === DrivewealthAccountManagementType.OpenBrokerage);
        },
        advisoryEmployerDefaultInstrumentList: state => {
            return _.find(state.employerDefaultInstrumentLists,
                x => x.listInvestmentOption === DrivewealthAccountManagementType.Advisory);
        },
        openBrokerageEmployerDefaultInstrumentList: state => {
            return _.find(state.employerDefaultInstrumentLists,
                x => x.listInvestmentOption === DrivewealthAccountManagementType.OpenBrokerage);
        },
        advisoryCustodianDefaultInstrumentList: state => {
            return _.find(state.custodianDefaultInstrumentLists,
                x => x.listInvestmentOption === DrivewealthAccountManagementType.Advisory);
        },
        openBrokerageCustodianDefaultInstrumentList: state => {
            return _.find(state.custodianDefaultInstrumentLists,
                x => x.listInvestmentOption === DrivewealthAccountManagementType.OpenBrokerage);
        }
    },
    actions: {
        setInstrumentListsFetchState (fetchState: FetchState) {
            this.instrumentListFetchState = fetchState;
        },
        setCuratedRtqInstrumentListsFetchState (fetchState: FetchState) {
            this.curatedRtqInstrumentListFetchState = fetchState;
        },
        setInstrumentDetailFetchState (fetchState: FetchState) {
            this.instrumentDetailFetchState = fetchState;
        },
        setInstrumentLists (collection: InstrumentList[]) {
            this.instrumentLists = collection;
        },
        setCuratedRtqInstrumentLists (collection: InstrumentList[]) {
            this.curatedRtqInstrumentLists = collection;
        },
        setInstrumentDetails (collection: InstrumentDetail[]) {
            this.instrumentDetails = collection;
        },
        setInstrumentUploadResponse (collection: InstrumentUploadResponse) {
            this.instrumentUploadResponse = collection;
        },
        setEmployerInstrumentListFetchState (fetchState: FetchState) {
            this.employerInstrumentListFetchState = fetchState;
        },
        setEmployerInstrumentList (list: PartnerEmployerInstrumentList) {
            this.employerInstrumentList = list;
        },
        setPartnerInstrumentListFetchState (fetchState: FetchState) {
            this.partnerInstrumentListFetchState = fetchState;
        },
        setPartnerInstrumentList (list: PartnerEmployerInstrumentList) {
            this.partnerInstrumentList = list;
        },
        setEmployerDefaultInstrumentLists (collection: InstrumentList[]) {
            this.employerDefaultInstrumentLists = collection;
        },
        setCustodianDefaultInstrumentLists (collection: InstrumentList[]) {
            this.custodianDefaultInstrumentLists = collection;
        },
        setEmployerWlpInstrumentListsFetchState (fetchState: FetchState) {
            this.wlpInstrumentListsFetchState = fetchState;
        },
        setCustodianWlpInstrumentListsFetchState (fetchState: FetchState) {
            this.custodianWlpInstrumentListsFetchState = fetchState;
        },
        loadInstrumentLists (): Promise<any> {
            if (this.instrumentListFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setInstrumentListsFetchState(FetchState.Loading);
            return drivewealthService.getInstrumentLists(ListType.None)
                .then(response => {
                    this.setInstrumentLists(response.data);
                    this.setInstrumentListsFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setInstrumentListsFetchState(FetchState.Uninitialized);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        loadCuratedRtqInstrumentLists (): Promise<any> {
            if (this.curatedRtqInstrumentListFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setCuratedRtqInstrumentListsFetchState(FetchState.Loading);
            return drivewealthService.getInstrumentLists(ListType.CuratedRtqList)
                .then(response => {
                    this.setCuratedRtqInstrumentLists(response.data);
                    this.setCuratedRtqInstrumentListsFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setCuratedRtqInstrumentListsFetchState(FetchState.Uninitialized);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        loadInstrumentList (instrumentListId): Promise<any> {
            this.setInstrumentDetailFetchState(FetchState.Loading);
            return drivewealthService.getInstrumentList(instrumentListId)
                .then(response => {
                    this.setInstrumentDetails(response.data);
                    this.setInstrumentDetailFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setInstrumentDetailFetchState(FetchState.Uninitialized);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        loadPartnerInstrumentList (partnerId: number): Promise<any> {
            this.setPartnerInstrumentListFetchState(FetchState.Loading);
            return drivewealthService.getPartnerInstrumentLists(partnerId)
                .then(response => {
                    this.setPartnerInstrumentList(response.data);
                    this.setPartnerInstrumentListFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setPartnerInstrumentListFetchState(FetchState.Uninitialized);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        loadEmployerDefaultInstrumentList (employerId: number): Promise<any> {
            return drivewealthService.getEmployerDefaultInstrumentLists(employerId)
                .then(response => {
                    this.setEmployerDefaultInstrumentLists(response.data);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        loadCustodianDefaultInstrumentList (custodianId: number): Promise<any> {
            return drivewealthService.getCustodianDefaultInstrumentLists(custodianId)
                .then(response => {
                    this.setCustodianDefaultInstrumentLists(response.data);
                })
                .catch(err => {
                    this.setCustodianDefaultInstrumentLists([]);
                });
        },
        beginSetPartnerInstrumentList ({ partnerId, advisoryListId, openBrokerageListId }:
                                           {
                                               partnerId: number, advisoryListId: number, openBrokerageListId
                                           }): Promise<any> {
            this.setPartnerInstrumentListFetchState(FetchState.Loading);
            return drivewealthService.setPartnerInstrumentLists(partnerId, advisoryListId, openBrokerageListId)
                .then(response => {
                    this.setPartnerInstrumentList(response.data);
                    this.setPartnerInstrumentListFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setPartnerInstrumentListFetchState(FetchState.Uninitialized);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        downloadInstrumentList (instrumentListId): Promise<any> {
            return drivewealthService.getInstrumentListCsv(instrumentListId)
                .then(response => {
                    const reportData = response.data;
                    let fileName = "instrument-list-" + instrumentListId + ".csv";
                    const match = response.headers["content-disposition"]
                        .match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/i);
                    if (match && match.length > 0) {
                        fileName = match[1];
                    }
                    downloadCsvFile(reportData, fileName);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        createInstrumentList (instrumentList: InstrumentList): Promise<any> {
            this.setInstrumentListsFetchState(FetchState.Loading);
            return drivewealthService.createInstrumentList(instrumentList)
                .then(response => {
                    this.setInstrumentLists(response.data);
                    this.setInstrumentListsFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        saveEmployerDefaultList (request: EmployerDrivewealthListRequest): Promise<any> {
            this.setEmployerWlpInstrumentListsFetchState(FetchState.Loading);
            return drivewealthService.saveEmployerDefaultList(request)
                .then(response => {
                    this.setEmployerWlpInstrumentListsFetchState(FetchState.Loaded);
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Default list sucessfully set.");
                })
                .catch(err => {
                    this.setEmployerWlpInstrumentListsFetchState(FetchState.Loaded);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        saveCustodianDefaultList (request: CustodianDrivewealthListRequest): Promise<any> {
            this.setCustodianWlpInstrumentListsFetchState(FetchState.Loading);
            return drivewealthService.saveCustodianDefaultList(request)
                .then(response => {
                    this.setCustodianWlpInstrumentListsFetchState(FetchState.Loaded);
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Default list sucessfully set.");
                })
                .catch(err => {
                    this.setCustodianWlpInstrumentListsFetchState(FetchState.Loaded);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        processInstrumentListFile ({ instrumentListId, csv }) {
            return drivewealthService.processInstrumentListFile(instrumentListId, csv)
                .then(response => {
                    this.setInstrumentListsFetchState(FetchState.Uninitialized);
                    const data = response.data;
                    const notifierStore = useNotifierStore();
                    const successText = "Instrument List processing complete."
                        + "<br/>Total: " + data.totalSymbols
                        + "<br/>Added: " + data.addedSymbols
                        + "<br/>Deleted: " + data.deletedSymbols
                        + "<br/>Unknown: " + data.unknownSymbols
                        + "<br/><a href=\"" + data.s3DownloadUrl
                        + "\" download style=\"color: white; text-decoration: underline;\">Download Details</a>";
                    notifierStore.setSuccess(successText);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        processInstrumentMetadataFile ({  csv }) {
            return drivewealthService.processInstrumentMetadataFile(csv)
                .then(response => {
                    this.setInstrumentListsFetchState(FetchState.Uninitialized);
                    const data = response.data;
                    const notifierStore = useNotifierStore();
                    const successText = "Instrument List processing complete."
                        + "<br/>Total: " + data.total
                        + "<br/>Added: " + data.added
                        + "<br/>Updated: " + data.updated
                        + "<br/>Deleted: " + data.deleted
                        + "<br/>No Change: " + data.noChange
                        + "<br/>Not Found: " + data.notFound
                        + "<br/><a href=\"" + data.s3DownloadUrl
                        + "\" download style=\"color: white; text-decoration: underline;\">Download Details</a>";
                    notifierStore.setSuccess(successText);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
    }
});